import { useEffect, useState } from "react"

import {
  partnerLandingPageAssessmentPath,
  partnerLandingPageAssessmentStatusPath,
} from "@/routes"

/** The site lookup's yield (Discovery::SiteLookup) — the business profile the
 *  conversation can speak from while the interview is still going. */
export interface LookupProfile {
  business_name: string
  vertical: string | null
  services: string[]
  hours: string | null
  has_contact_form: boolean
  has_booking_link: boolean
  has_chat_widget: boolean
}

export type LookupStatus = "pending" | "succeeded" | "failed"

export interface Lookup {
  status: LookupStatus
  profile: LookupProfile | null
  /** The assessment record's uid — the conversation's endpoints (demo call,
   *  turn, gaps, complete) post against it. */
  assessmentUid: string | null
}

const POLL_MS = 2500
// A lookup that outlives the whole interview is a failed lookup: the close
// can't wait forever, and the failure path is designed to be survivable.
const TIMEOUT_MS = 90_000

function csrfToken(): string {
  return (
    document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content") ?? ""
  )
}

/**
 * IMP-503 follow-up: creates the assessment at URL-submit time, BEFORE the
 * overlay opens — the server runs its free pre-screen (sanitizer + hostname
 * denylist) synchronously, so a disqualifying URL dies at the URL field with
 * a neutral message and never becomes a conversation.
 *
 * Returns the assessment uid on success, or the server's visitor-facing
 * message on rejection. `company` is the hidden honeypot field's value.
 */
export async function startAssessment(
  landingUid: string,
  website: string,
  company: string,
): Promise<{ assessmentUid: string } | { error: string }> {
  try {
    const response = await fetch(partnerLandingPageAssessmentPath(landingUid), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken(),
        Accept: "application/json",
      },
      credentials: "same-origin",
      body: JSON.stringify({ website, company }),
    })
    const data = (await response.json().catch(() => ({}))) as {
      assessment_uid?: string
      error?: string
    }
    if (response.ok && data.assessment_uid) {
      return { assessmentUid: data.assessment_uid }
    }
    return { error: data.error ?? "Something went wrong — try again." }
  } catch {
    return { error: "Something went wrong — try again." }
  }
}

/**
 * IMP-503: polls the background site lookup the assessment is running. The
 * conversation reads `status` to time its announcement and to pick the close
 * (a failed lookup closes the demo path: no test call, no receptionist, gaps
 * straight to the email ask).
 */
export function useAssessmentLookup(
  landingUid: string,
  assessmentUid: string,
): Lookup {
  const [lookup, setLookup] = useState<Lookup>({
    status: "pending",
    profile: null,
    assessmentUid,
  })

  // Each effect run owns its whole lifecycle and dies with its cleanup — no
  // "already started" ref guard: refs survive StrictMode's mount-unmount-mount
  // (and fast refresh), so a guarded effect ends up with the first run
  // cancelled by cleanup and the second run skipped by the guard, and nothing
  // ever polls.
  useEffect(() => {
    let cancelled = false
    let timer: number | undefined
    const startedAt = Date.now()

    const fail = () => {
      if (!cancelled)
        setLookup((prev) => ({ ...prev, status: "failed", profile: null }))
    }

    const poll = () => {
      if (cancelled) return
      if (Date.now() - startedAt > TIMEOUT_MS) return fail()
      timer = window.setTimeout(async () => {
        try {
          const response = await fetch(
            partnerLandingPageAssessmentStatusPath(landingUid, assessmentUid),
            { headers: { Accept: "application/json" } },
          )
          if (!response.ok) throw new Error(String(response.status))
          const data = (await response.json()) as {
            status: LookupStatus
            profile?: LookupProfile
          }
          if (cancelled) return
          if (data.status === "succeeded" && data.profile) {
            setLookup({
              status: "succeeded",
              profile: data.profile,
              assessmentUid,
            })
          } else if (data.status === "failed") {
            setLookup({ status: "failed", profile: null, assessmentUid })
          } else {
            poll()
          }
        } catch {
          // A flaky poll isn't a failed lookup — keep trying until timeout.
          poll()
        }
      }, POLL_MS)
    }

    poll()

    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return lookup
}
